import { logger } from '@/shared/utils';
import { mediaStorage } from '@/features/indexedDB';
import { cancelMediaDownload, downloadMedia } from '@/infrastructure/sdk';
import { DOWNLOAD_STATUS, UPLOAD_STATUS } from '@/shared/constants';
import { getMessageFromStore } from '@/features/chats/model/conversation/selectors';
import { updateMediaUploadStatus } from './queueUtils';

const downloadQueue = [];
const pendingMsgIds = new Set();
let isProcessing = false;

const processQueue = async () => {
   if (isProcessing || downloadQueue.length === 0) return;
   isProcessing = true;
   const { msgId, task, resolve, reject } = downloadQueue[0];
   try {
      resolve(await task());
   } catch (err) {
      reject(err);
   } finally {
      pendingMsgIds.delete(msgId);
      downloadQueue.shift();
      isProcessing = false;
      processQueue();
   }
};

const enqueueTask = (msgId, task) => {
   if (pendingMsgIds.has(msgId)) return Promise.resolve({ statusCode: 202, message: 'Already in progress' });
   pendingMsgIds.add(msgId);
   return new Promise((resolve, reject) => {
      downloadQueue.push({ msgId, task, resolve, reject });
      processQueue();
   });
};

const executeSdkDownload = msgId => new Promise((resolve, reject) => downloadMedia(msgId, () => {}, resolve, reject));

const getCachedMedia = async (msgId, chatId) => {
   const stored = await mediaStorage.get(msgId);
   if (stored?.blob) return { blob: stored.blob, blobUrl: null };
   const blobUrl = getMessageFromStore(chatId, msgId)?.msgBody?.media?.blobUrl;
   if (blobUrl) return { blob: null, blobUrl };

   return null;
};

const triggerBrowserSave = (blob, fileName) => {
   const url = URL.createObjectURL(blob);
   const anchor = Object.assign(document.createElement('a'), { href: url, download: fileName, style: 'display:none' });
   document.body.appendChild(anchor);
   anchor.click();
   setTimeout(() => {
      URL.revokeObjectURL(url);
      document.body.removeChild(anchor);
   }, 100);
};

const resolveBlob = async (msgId, chatId) => {
   const cached = await getCachedMedia(msgId, chatId);
   if (cached?.blob) return cached.blob;
   if (cached?.blobUrl) return fetch(cached.blobUrl).then(r => r.blob());

   updateMediaUploadStatus({
      chatId,
      msgId,
      is_downloaded: DOWNLOAD_STATUS.IN_PROGRESS,
      is_uploading: UPLOAD_STATUS.COMPLETED,
   });

   const response = await executeSdkDownload(msgId);
   const blob = response?.blob;

   if (!blob) {
      updateMediaUploadStatus({
         chatId,
         msgId,
         is_downloaded: DOWNLOAD_STATUS.FAILED,
         is_uploading: UPLOAD_STATUS.COMPLETED,
      });
      throw new Error('Failed to download media');
   }

   const blobUrl = response.blobUrl ?? URL.createObjectURL(blob);
   updateMediaUploadStatus({
      chatId,
      msgId,
      blobUrl,
      is_downloaded: DOWNLOAD_STATUS.COMPLETED,
      is_uploading: UPLOAD_STATUS.COMPLETED,
   });

   return blob;
};

export const downloadMediaAndTriggerSave = (chatId, msgId) =>
   enqueueTask(msgId, async () => {
      try {
         const fileName = getMessageFromStore(chatId, msgId)?.msgBody?.media?.fileName || 'downloaded_file';
         const blob = await resolveBlob(msgId, chatId);
         triggerBrowserSave(blob, fileName);
         return { statusCode: 200, message: 'Download triggered successfully' };
      } catch (err) {
         logger.error('[DownloadQueue] TriggerSave Error:', err);
         return { statusCode: 500, error: err.message };
      }
   });

export const executeDownload = async (msgId, chatId, shouldCache) => {
   const cached = await getCachedMedia(msgId, chatId);
   if (cached?.blob) return { statusCode: 200, blobUrl: URL.createObjectURL(cached.blob) };
   if (cached?.blobUrl) return { statusCode: 200, blobUrl: cached.blobUrl };

   return enqueueTask(msgId, async () => {
      try {
         const response = await executeSdkDownload(msgId);
         if (shouldCache && response?.blob) {
            mediaStorage
               .save(msgId, response.blob)
               .catch(err => logger.error('[DownloadQueue] Cache save error:', err));
         }
         return { statusCode: 200, ...response };
      } catch (err) {
         logger.error('[DownloadQueue] SDK Error:', err);
         return { statusCode: 500, error: err.message };
      }
   });
};

export const cancelDownload = async (msgId, chatId) => {
   await cancelMediaDownload(msgId);
   pendingMsgIds.delete(msgId);
   updateMediaUploadStatus({ chatId, msgId, is_downloaded: DOWNLOAD_STATUS.FAILED });
};
